import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
	Input,
	Switch,
	Drawer,
	Spin,
	Select,
	Tooltip,
	Row,
	Col,
	DatePicker,
	Modal,
	message,
	Button,
} from 'antd';
import {
	ExportOutlined,
	RedoOutlined,
	PlusOutlined,
	EyeOutlined,
	FilePdfOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex, Text } from 'atoms';
import { FilterTitle } from 'components/common';
import FilterTag from 'components/common/FilterTag';
import {
	getVendorContractListV2,
	searchVehicleTypesV2,
	updateVendorContractV2,
	exportVendorContractCSVV2,
	searchVendorFiltersV2,
} from 'store/vendorSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { searchRouteFiltersV2 } from 'store/routeSliceV2';
import { getVCF } from 'store/vendorBillSlice';
import { usePersistedState } from 'hooks';

// Filter Options Constants
const StatusOptions = [
	{ value: true, label: 'Active' },
	{ value: false, label: 'Inactive' },
];

const ApprovalStatusOptions = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'approved', label: 'Approved' },
	{ value: 'rejected', label: 'Rejected' },
];

const MultipleTripsOptions = [
	{ value: true, label: 'Yes' },
	{ value: false, label: 'No' },
];

const { Search } = Input;
const { RangePicker } = DatePicker;
const FILTER_DATE_FORMAT = 'YYYY-MM-DD';

const LtSearchBar = styled(Search)`
	border: none;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 16px;
`;

const ViewButton = styled(ButtonWrapper)`
	padding: 4px;
	min-width: 32px;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

const pdf = 'pdf';
const docx = 'docx';

const handleVCFDownloadClick = ({ dispatch, id, type }) => dispatch(getVCF({ id, type }));

const formatContractDate = (value) => (value ? moment(value).format('DD-MM-YYYY') : '-');

// Format a numeric amount as INR; '-' when missing/invalid.
const formatINR = (value) => {
	const n = Number(value);
	if (value == null || Number.isNaN(n)) return '-';
	return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper function to transform snake_case API response to camelCase
const transformContractData = (contract) => {
	if (!contract) return null;

	return {
		id: contract.id,
		vendorId: contract.vendor_id,
		vendorName: contract.vendor_name || '-',
		projectName: contract.project_code || contract.project_name || '-',
		billingType: contract.billing_type || '-',
		billingSubtype: contract.billing_subtype || '-',
		vehicleType: contract.vehicle_type_name || '-',
		fixedVehicleRate: contract.fixed_vehicle_rate,
		noOfVehicles:
			contract.selected_no_of_vehicles ||
			contract.no_of_vehicles ||
			contract.number_of_vehicles ||
			'-',
		contractDate: formatContractDate(contract.contract_date),
		expiryDate: formatContractDate(contract.expiry_date),
		reportingTime: contract.reporting_time || '-',
		multipleTrips: contract.multiple_trips ? 'Yes' : 'No',
		createdBy: contract.created_by_name || contract.created_by || contract.approver_name || '-',
		status: contract.is_active ?? false,
		approvalStatus: contract.approval_status || 'pending',
		hasPendingEdit: contract.has_pending_edit ?? false,
		routeName: contract.route_name || '-',
		routeCode: contract.route_code || '',
		createdAt: contract.created_at,
		approvedAt: contract.approved_at,
		approverName: contract.approver_name,
	};
};

const getTableColumns = (navigate, handleStatusToggle, dispatch) => [
	{
		title: 'Vendor Name',
		dataIndex: 'vendorName',
		key: 'vendorName',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Project Name',
		dataIndex: 'projectName',
		key: 'projectName',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Billing Type',
		dataIndex: 'billingType',
		key: 'billingType',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Sub Billing Type',
		dataIndex: 'billingSubtype',
		key: 'billingSubtype',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Vehicle Type',
		dataIndex: 'vehicleType',
		key: 'vehicleType',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Fixed Vehicle Rate',
		dataIndex: 'fixedVehicleRate',
		key: 'fixedVehicleRate',
		render: (value) => <Text>{formatINR(value)}</Text>,
	},
	{
		title: 'Approval Status',
		dataIndex: 'approvalStatus',
		key: 'approvalStatus',
		render: (status, record) => {
			if (record?.hasPendingEdit) {
				return (
					<Text style={{ color: '#faad14', fontWeight: 500 }}>Pending Edit Approval</Text>
				);
			}
			const statusColors = {
				pending: '#faad14',
				approved: '#52c41a',
				rejected: '#ff4d4f',
			};
			return (
				<Text style={{ color: statusColors[status] || '#000', fontWeight: 500 }}>
					{status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
				</Text>
			);
		},
	},
	{
		title: 'No. of Vehicles',
		dataIndex: 'noOfVehicles',
		key: 'noOfVehicles',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Contract Date',
		dataIndex: 'contractDate',
		key: 'contractDate',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Expiry Date',
		dataIndex: 'expiryDate',
		key: 'expiryDate',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Reporting Time',
		dataIndex: 'reportingTime',
		key: 'reportingTime',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Multiple Trips',
		dataIndex: 'multipleTrips',
		key: 'multipleTrips',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Created by',
		dataIndex: 'createdBy',
		key: 'createdBy',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Status',
		dataIndex: 'status',
		key: 'status',
		render: (status, record) => (
			<Switch
				key={`status-${record.id}-${status}`}
				checked={status}
				checkedChildren="Active"
				unCheckedChildren="Inactive"
				disabled={record.hasPendingEdit}
				onChange={() => handleStatusToggle(record)}
			/>
		),
	},
	{
		title: 'VCF',
		dataIndex: 'id',
		key: 'vcf',
		render: (id) => (
			<Row gutter={16}>
				<Col>
					<Tooltip title="Download VCF">
						<FilePdfOutlined
							style={{
								color: 'red',
								fontSize: '14px',
							}}
							onClick={() => handleVCFDownloadClick({ dispatch, id, type: pdf })}
						/>
					</Tooltip>
				</Col>
				<Col>
					<Tooltip title="Download DOC">
						<FilePdfOutlined
							style={{
								color: 'blue',
								fontSize: '14px',
							}}
							onClick={() => handleVCFDownloadClick({ dispatch, id, type: docx })}
						/>
					</Tooltip>
				</Col>
			</Row>
		),
	},
	{
		title: 'View',
		dataIndex: 'id',
		key: 'view',
		render: (id, record) => (
			<ViewButton
				onClick={() =>
					navigate(`/vendor-contracts/${id}`, { state: { vendorId: record?.vendorId } })
				}
			>
				<EyeOutlined style={{ fontSize: 18, color: '#1890ff' }} />
			</ViewButton>
		),
	},
];

// Fields shown for the contract that is blocking activation (collision modal).
const CONFLICT_FIELDS = [
	{ key: 'vendor_name', label: 'Vendor' },
	{ key: 'project_code', label: 'Project Code' },
	{ key: 'project_name', label: 'Project' },
	{ key: 'vehicle_type_name', label: 'Vehicle Type' },
	{ key: 'route_name', label: 'Route' },
	{ key: 'route_code', label: 'Route Code' },
	{ key: 'contract_date', label: 'Contract Date' },
	{ key: 'expiry_date', label: 'Expiry Date' },
	{ key: 'billing_type', label: 'Billing Type' },
];

export default function VendorContractListPageV2() {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
	const [searchValue, setSearchValue] = usePersistedState('vendorContractList:search', ''); // Input value
	const [searchTerm, setSearchTerm] = usePersistedState('vendorContractList:searchTerm', ''); // Applied search term
	const [loading, setLoading] = useState(false);
	const [contractsData, setContractsData] = useState([]);
	const [pagination, setPagination] = usePersistedState('vendorContractList:pagination', {
		current: 1,
		pageSize: 20,
		total: 0,
	});
	const [filters, setFilters] = usePersistedState('vendorContractList:filters', {});
	const [contractDateRange, setContractDateRange] = useState(null);
	const [expiryDateRange, setExpiryDateRange] = useState(null);

	// Filter Options State
	const [filterOptions, setFilterOptions] = useState({
		vendors: [],
		projects: [],
		vehicleTypes: [],
		routes: [],
	});

	// Fetch all filter options (vendors, projects, vehicle types, routes)
	const fetchFilterOptions = useCallback(async () => {
		try {
			// 1. Fetch Vehicle Types
			const vehicleTypePromise = dispatch(
				searchVehicleTypesV2({
					filter_by: {},
					limit: 1000,
				})
			);

			// 2. Fetch Vendors
			const vendorPromise = dispatch(
				searchVendorFiltersV2({
					limit: 100,
				})
			);

			// 3. Fetch Projects
			const projectPromise = dispatch(
				getProjectListV2({
					pagination: { page: 1, per_page: 1000 },
				})
			);

			// 4. Fetch Routes
			const routePromise = dispatch(
				searchRouteFiltersV2({
					filter_by: {},
					limit: 1000,
				})
			);

			const [vehicleResponse, vendorResponse, projectResponse, routeResponse] =
				await Promise.all([
					vehicleTypePromise,
					vendorPromise,
					projectPromise,
					routePromise,
				]);

			setFilterOptions((prev) => {
				const newOptions = { ...prev };

				if (vehicleResponse?.rows && vehicleResponse.rows.length > 0) {
					newOptions.vehicleTypes = vehicleResponse.rows.map((item) => ({
						value: item.id || item.vehicle_type_id || item.vehicle_type,
						label:
							item.vehicle_type_name ||
							item.vehicle_type ||
							item.name ||
							item.label ||
							'-',
					}));
				}

				if (vendorResponse?.results) {
					newOptions.vendors = vendorResponse.results.map((item) => ({
						value: item.value, // value is id returned from searchVendorFiltersV2
						label: item.label,
					}));
				}

				if (projectResponse?.rows) {
					newOptions.projects = projectResponse.rows.map((project) => ({
						value: project.id,
						label: project.project_code || project.project_name || '-',
					}));
				}

				if (routeResponse?.results) {
					newOptions.routes = routeResponse.results.map((item) => ({
						value: item.value || item.id,
						label: item.label || item.route_name || '-',
					}));
				}

				return newOptions;
			});
		} catch (error) {
			console.error('Error fetching filter options:', error);
		}
	}, [dispatch]);

	// Fetch filters when drawer opens
	useEffect(() => {
		if (filterDrawerOpen && filterOptions.vendors.length === 0) {
			fetchFilterOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterDrawerOpen]);

	// Handle dynamic search for vendors
	const handleVendorSearch = (value) => {
		if (window.vendorSearchTimeout) {
			clearTimeout(window.vendorSearchTimeout);
		}
		window.vendorSearchTimeout = setTimeout(async () => {
			try {
				let vendorResponse;
				if (!value) {
					vendorResponse = await dispatch(
						searchVendorFiltersV2({
							limit: 100,
						})
					);
				} else {
					vendorResponse = await dispatch(
						searchVendorFiltersV2({
							filter_by: { query_str: value },
							limit: 100,
						})
					);
				}

				if (vendorResponse?.results) {
					setFilterOptions((prev) => ({
						...prev,
						vendors: vendorResponse.results.map((item) => ({
							value: item.value,
							label: item.label,
						})),
					}));
				}
			} catch (error) {
				console.error('Error searching dynamic vendors:', error);
			}
		}, 600);
	};

	// Fetch vendor contracts on mount and when pagination/filters/search change
	useEffect(() => {
		fetchVendorContracts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination.current, pagination.pageSize, filters, searchTerm]);

	/**
	 * Build filter_by object for API
	 * Maps search term to query_str key for general search across vendor contract fields
	 * Expected payload: {search_type: "vendor_contract", filter_by: {query_str: "..."}, pagination: {page: 1, per_page: 20}}
	 */
	const buildFilterBy = () => ({
		// Map search term to query_str for general search across vendor contract fields
		...(searchTerm && { query_str: searchTerm }),
		...(filters?.is_active !== undefined && { is_active: filters.is_active }),
		...(filters?.approval_status && { approval_status: filters.approval_status }),
		...(filters?.vendor_id && { vendor_id: filters.vendor_id }),
		...(filters?.project_id && { project_id: filters.project_id }),
		...(filters?.billing_type && { billing_type: filters.billing_type }),
		...(filters?.vehicle_type_id && { vehicle_type_id: filters.vehicle_type_id }),
		...(filters?.route_id && { route_id: filters.route_id }),
		...(filters?.multiple_trips !== undefined && { multiple_trips: filters.multiple_trips }),
		...(filters?.contract_date_from && { contract_date_from: filters.contract_date_from }),
		...(filters?.contract_date_to && { contract_date_to: filters.contract_date_to }),
		...(filters?.expiry_date_from && { expiry_date_from: filters.expiry_date_from }),
		...(filters?.expiry_date_to && { expiry_date_to: filters.expiry_date_to }),
	});

	const fetchVendorContracts = async () => {
		setLoading(true);
		try {
			const filterBy = buildFilterBy();
			const response = await dispatch(
				getVendorContractListV2({
					filter_by: filterBy || {},
					pagination: {
						page: pagination.current,
						per_page: pagination.pageSize,
					},
				})
			);

			if (response) {
				// Transform snake_case to camelCase
				const transformedData = (response.rows || []).map(transformContractData);
				setContractsData(transformedData);
				setPagination((prev) => ({
					...prev,
					total: response.count || 0,
				}));

				// Filter options are fetched independently when the filter drawer opens.
			}
		} catch (error) {
			console.error('Error fetching vendor contracts:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		const trimmedValue = value?.trim() || '';
		setSearchValue(trimmedValue);
		setSearchTerm(trimmedValue); // Update the actual search term used for filtering
		setPagination((prev) => ({
			...prev,
			current: 1, // Reset to first page when search changes
		}));
	};

	const handleTableChange = (paginationConfig) => {
		setPagination({
			current: paginationConfig.current,
			pageSize: paginationConfig.pageSize,
			total: paginationConfig.total,
		});
	};

	const handleExport = async () => {
		try {
			// Use current filters and search term for export
			const filterBy = buildFilterBy();
			await dispatch(
				exportVendorContractCSVV2({
					filter_by: filterBy || {},
					format: 'csv',
				})
			);
		} catch (error) {
			console.error('Error exporting vendor contracts:', error);
		}
	};

	const handleSync = () => {
		fetchVendorContracts();
	};

	const handleCreate = () => {
		navigate('/vendor-contracts/add');
	};

	const handleFilterChange = (filterKey, value) => {
		setFilters((prev) => {
			const newFilters = { ...prev };
			// Handle boolean false values - they should be kept
			if (typeof value === 'boolean') {
				newFilters[filterKey] = value;
			} else if (value === undefined || value === null || value === '') {
				delete newFilters[filterKey];
			} else {
				newFilters[filterKey] = value;
			}
			return newFilters;
		});
		setPagination((prev) => ({
			...prev,
			current: 1, // Reset to first page when filter changes
		}));
	};

	const handleResetFilters = () => {
		setFilters({});
		setContractDateRange(null);
		setExpiryDateRange(null);
		setSearchValue('');
		setSearchTerm('');
		setPagination((prev) => ({
			...prev,
			current: 1,
		}));
		setFilterDrawerOpen(false);
	};

	// Deactivate reason modal state — used when an approved contract is being
	// toggled from active → inactive (routes through the edit-flow approval).
	const [deactivateModal, setDeactivateModal] = useState({ open: false, record: null });
	const [deactivateReason, setDeactivateReason] = useState('');
	const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);

	// Activation collision modal — shown when the backend blocks activation because
	// another active contract overlaps (error code ACTIVATION_BLOCKED_BY_COLLISION).
	const [collisionModal, setCollisionModal] = useState({
		open: false,
		message: '',
		conflict: null,
	});

	// Handle status toggle
	const handleStatusToggle = async (record) => {
		const contractId = record?.id;
		const currentStatus = record?.status;
		const isApproved = record?.approvalStatus === 'approved';
		const newStatus = !currentStatus;

		// Approved + active → inactive: gather reason and submit via edit_flow.
		if (isApproved && currentStatus === true) {
			setDeactivateModal({ open: true, record });
			setDeactivateReason('');
			return;
		}

		try {
			await dispatch(
				updateVendorContractV2({
					req_data: {
						id: contractId,
						is_active: newStatus,
					},
				})
			);
			// Optimistically update the local state
			setContractsData((prevData) =>
				prevData.map((contract) =>
					contract.id === contractId ? { ...contract, status: newStatus } : contract
				)
			);
			// Refresh the contract list to ensure data consistency
			await fetchVendorContracts();
		} catch (error) {
			const serverError = error?.response?.data?.error;
			if (serverError?.code === 'ACTIVATION_BLOCKED_BY_COLLISION') {
				setCollisionModal({
					open: true,
					message: serverError?.message || '',
					conflict: serverError?.conflicting_contract || null,
				});
			} else {
				console.error('Error updating vendor contract status:', error);
			}
			// Revert by refreshing
			await fetchVendorContracts();
		}
	};

	const handleCollisionClose = () =>
		setCollisionModal({ open: false, message: '', conflict: null });

	const handleViewConflicting = () => {
		const { conflict } = collisionModal;
		if (!conflict?.id) return;
		handleCollisionClose();
		navigate(`/vendor-contracts/${conflict.id}`, {
			state: { vendorId: conflict?.vendor_id },
		});
	};

	const handleDeactivateCancel = () => {
		if (deactivateSubmitting) return;
		setDeactivateModal({ open: false, record: null });
		setDeactivateReason('');
	};

	const handleDeactivateConfirm = async () => {
		const reason = (deactivateReason || '').trim();
		if (!reason) {
			message.error('Reason is required');
			return;
		}
		const target = deactivateModal.record;
		if (!target?.id) return;

		setDeactivateSubmitting(true);
		try {
			const response = await dispatch(
				updateVendorContractV2({
					req_data: { id: target.id },
					edit_flow: true,
					comments: reason,
					changes: { is_active: false },
				})
			);
			if (response?.status === 'success' || response?.data) {
				message.success(response?.message || 'Deactivation submitted for approval');
				setDeactivateModal({ open: false, record: null });
				setDeactivateReason('');
				await fetchVendorContracts();
			} else {
				message.error(response?.message || 'Failed to submit deactivation');
			}
		} catch (error) {
			const errMsg =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				'Failed to submit deactivation';
			message.error(errMsg);
		} finally {
			setDeactivateSubmitting(false);
		}
	};

	// Helper functions to check if filters are applied
	const hasStatusFilter = () => filters.is_active !== undefined && filters.is_active !== null;
	const hasApprovalStatusFilter = () =>
		filters.approval_status !== undefined && filters.approval_status !== null;
	const hasVendorFilter = () => filters.vendor_id !== undefined && filters.vendor_id !== null;
	const hasProjectFilter = () => filters.project_id !== undefined && filters.project_id !== null;
	const hasBillingTypeFilter = () =>
		filters.billing_type !== undefined && filters.billing_type !== null;
	const hasVehicleTypeFilter = () =>
		filters.vehicle_type_id !== undefined && filters.vehicle_type_id !== null;
	const hasRouteFilter = () => filters.route_id !== undefined && filters.route_id !== null;
	const hasMultipleTripsFilter = () =>
		filters.multiple_trips !== undefined && filters.multiple_trips !== null;
	const hasContractDateRangeFilter = () =>
		contractDateRange && contractDateRange[0] && contractDateRange[1];
	const hasExpiryDateRangeFilter = () =>
		expiryDateRange && expiryDateRange[0] && expiryDateRange[1];

	const activeFiltersCount = [
		hasStatusFilter(),
		hasApprovalStatusFilter(),
		hasVendorFilter(),
		hasProjectFilter(),
		hasBillingTypeFilter(),
		hasVehicleTypeFilter(),
		hasRouteFilter(),
		hasMultipleTripsFilter(),
		hasContractDateRangeFilter(),
		hasExpiryDateRangeFilter(),
	].filter(Boolean).length;

	// Helper function to remove a specific filter
	const removeFilter = (filterKey) => {
		setFilters((prev) => {
			const newFilters = { ...prev };
			if (filterKey === 'contract_date_range') {
				delete newFilters.contract_date_from;
				delete newFilters.contract_date_to;
			} else if (filterKey === 'expiry_date_range') {
				delete newFilters.expiry_date_from;
				delete newFilters.expiry_date_to;
			} else {
				delete newFilters[filterKey];
			}
			return newFilters;
		});
		if (filterKey === 'contract_date_range') {
			setContractDateRange(null);
		}
		if (filterKey === 'expiry_date_range') {
			setExpiryDateRange(null);
		}
		setPagination((prev) => ({
			...prev,
			current: 1,
		}));
	};

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mt={2} mb={2}>
				<Flex alignItems="center" gap="10px" flex="1">
					<Box flex="0 0 20%">
						<LtSearchBar
							placeholder="Search vendor contracts..."
							enterButton="Search"
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => {
								const val = e.target.value;
								setSearchValue(val);
								// Clear search term when input is cleared
								if (!val || !val.trim()) {
									setSearchTerm('');
									setPagination((prev) => ({
										...prev,
										current: 1,
									}));
								}
							}}
						/>
					</Box>
					<StyledFilterButton onClick={() => setFilterDrawerOpen(true)}>
						FILTERS
					</StyledFilterButton>
					{/* Filter Bar - Show active filters (excluding search) */}
					<FilterDisplayBox>
						{hasStatusFilter() && (
							<FilterTag onClose={() => removeFilter('is_active')}>Status</FilterTag>
						)}
						{hasApprovalStatusFilter() && (
							<FilterTag onClose={() => removeFilter('approval_status')}>
								Approval Status
							</FilterTag>
						)}
						{hasVendorFilter() && (
							<FilterTag onClose={() => removeFilter('vendor_id')}>Vendor</FilterTag>
						)}
						{hasProjectFilter() && (
							<FilterTag onClose={() => removeFilter('project_id')}>
								Project
							</FilterTag>
						)}
						{hasBillingTypeFilter() && (
							<FilterTag onClose={() => removeFilter('billing_type')}>
								Billing Type
							</FilterTag>
						)}
						{hasVehicleTypeFilter() && (
							<FilterTag onClose={() => removeFilter('vehicle_type_id')}>
								Vehicle Type
							</FilterTag>
						)}
						{hasRouteFilter() && (
							<FilterTag onClose={() => removeFilter('route_id')}>Route</FilterTag>
						)}
						{hasMultipleTripsFilter() && (
							<FilterTag onClose={() => removeFilter('multiple_trips')}>
								Multiple Trips
							</FilterTag>
						)}
						{hasContractDateRangeFilter() && (
							<FilterTag onClose={() => removeFilter('contract_date_range')}>
								Contract Date
							</FilterTag>
						)}
						{hasExpiryDateRangeFilter() && (
							<FilterTag onClose={() => removeFilter('expiry_date_range')}>
								Expiry Date
							</FilterTag>
						)}
						{activeFiltersCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={handleResetFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper onClick={handleExport} icons={<ExportOutlined />}>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
						CREATE
					</ButtonWrapper>
				</Flex>
			</Flex>

			<Box mt={3}>
				<Spin spinning={loading}>
					<AntdTable
						rowKey="id"
						columns={getTableColumns(navigate, handleStatusToggle, dispatch)}
						dataSource={contractsData}
						pagination={{
							current: pagination.current,
							pageSize: pagination.pageSize,
							total: pagination.total,
							showSizeChanger: true,
							pageSizeOptions: ['10', '20', '50', '100'],
							showTotal: (total) => `Total ${total} contracts`,
						}}
						onChange={handleTableChange}
					/>
				</Spin>
			</Box>

			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setFilterDrawerOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setFilterDrawerOpen(false)}
				open={filterDrawerOpen}
			>
				<Box>
					{/* Status Filter */}
					<Box mb="2rem">
						<Text mb="5px">Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="is_active"
							value={filters.is_active}
							onChange={(val) => handleFilterChange('is_active', val)}
							allowClear
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >=
								0
							}
							placeholder="Select"
							options={StatusOptions}
						/>
					</Box>

					{/* Approval Status Filter */}
					<Box mb="2rem">
						<Text mb="5px">Approval Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="approval_status"
							value={filters.approval_status}
							onChange={(val) => handleFilterChange('approval_status', val)}
							allowClear
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >=
								0
							}
							placeholder="Select"
							options={ApprovalStatusOptions}
						/>
					</Box>

					{/* Vendor Filter */}
					<Box mb="2rem">
						<Text mb="5px">Vendor</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vendor_id"
							value={filters.vendor_id}
							onChange={(val) => {
								handleFilterChange('vendor_id', val);
								if (!val) {
									handleVendorSearch('');
								}
							}}
							allowClear
							filterOption={false}
							onSearch={handleVendorSearch}
							placeholder="Search Vendor"
							options={filterOptions.vendors}
						/>
					</Box>

					{/* Project Filter */}
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_id"
							value={filters.project_id}
							onChange={(val) => handleFilterChange('project_id', val)}
							allowClear
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >=
								0
							}
							placeholder="Select Project"
							options={filterOptions.projects}
						/>
					</Box>

					{/* Billing Type Filter */}
					<Box mb="2rem">
						<Text mb="5px">Billing Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="billing_type"
							value={filters.billing_type}
							onChange={(val) => handleFilterChange('billing_type', val)}
							allowClear
							placeholder="Select Billing Type"
							options={[
								{ value: 'Fixed', label: 'Fixed' },
								{ value: 'Variable', label: 'Variable' },
								{ value: 'Fixed + Variable', label: 'Fixed + Variable' },
								{ value: 'Fixed Per Day', label: 'Fixed Per Day' },
							]}
						/>
					</Box>

					{/* Vehicle Type Filter */}
					<Box mb="2rem">
						<Text mb="5px">Vehicle Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vehicle_type_id"
							value={filters.vehicle_type_id}
							onChange={(val) => handleFilterChange('vehicle_type_id', val)}
							allowClear
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >=
								0
							}
							placeholder="Select Vehicle Type"
							options={filterOptions.vehicleTypes}
							notFoundContent={
								filterOptions.vehicleTypes.length === 0
									? 'No vehicle types found'
									: null
							}
						/>
					</Box>

					{/* Route Filter */}
					<Box mb="2rem">
						<Text mb="5px">Route</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="route_id"
							value={filters.route_id}
							onChange={(val) => handleFilterChange('route_id', val)}
							allowClear
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >=
								0
							}
							placeholder="Select Route"
							options={filterOptions.routes}
							notFoundContent={
								filterOptions.routes.length === 0 ? 'No routes found' : null
							}
						/>
					</Box>

					<Box mb="2rem">
						<Text mb="5px">Multiple Trips</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="multiple_trips"
							value={filters.multiple_trips}
							onChange={(val) => handleFilterChange('multiple_trips', val)}
							allowClear
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) >=
								0
							}
							placeholder="Select Multiple Trips"
							options={MultipleTripsOptions}
						/>
					</Box>

					<Box mb="2rem">
						<Text mb="5px">Contract Date</Text>
						<RangePicker
							style={{ width: '100%' }}
							format="DD-MM-YYYY"
							value={contractDateRange}
							onChange={(dates) => {
								setContractDateRange(dates);
								if (dates && dates[0] && dates[1]) {
									handleFilterChange(
										'contract_date_from',
										moment(dates[0]).format(FILTER_DATE_FORMAT)
									);
									handleFilterChange(
										'contract_date_to',
										moment(dates[1]).format(FILTER_DATE_FORMAT)
									);
								} else {
									handleFilterChange('contract_date_from', undefined);
									handleFilterChange('contract_date_to', undefined);
								}
							}}
						/>
					</Box>

					<Box mb="2rem">
						<Text mb="5px">Expiry Date</Text>
						<RangePicker
							style={{ width: '100%' }}
							format="DD-MM-YYYY"
							value={expiryDateRange}
							onChange={(dates) => {
								setExpiryDateRange(dates);
								if (dates && dates[0] && dates[1]) {
									handleFilterChange(
										'expiry_date_from',
										moment(dates[0]).format(FILTER_DATE_FORMAT)
									);
									handleFilterChange(
										'expiry_date_to',
										moment(dates[1]).format(FILTER_DATE_FORMAT)
									);
								} else {
									handleFilterChange('expiry_date_from', undefined);
									handleFilterChange('expiry_date_to', undefined);
								}
							}}
						/>
					</Box>

					{/* Reset Filters Button */}
					<Box>
						<ButtonWrapper onClick={handleResetFilters} style={{ width: '100%' }}>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>

			<Modal
				title="Deactivate Contract"
				open={deactivateModal.open}
				onCancel={handleDeactivateCancel}
				onOk={handleDeactivateConfirm}
				okText="Submit"
				cancelText="Cancel"
				confirmLoading={deactivateSubmitting}
				maskClosable={!deactivateSubmitting}
				closable={!deactivateSubmitting}
			>
				<Text style={{ display: 'block', marginBottom: 8 }}>
					Deactivating an approved contract requires approval. Please enter a reason.
				</Text>
				<Text style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
					Reason <span style={{ color: '#ff4d4f' }}>*</span>
				</Text>
				<Input.TextArea
					rows={4}
					value={deactivateReason}
					onChange={(e) => setDeactivateReason(e.target.value)}
					placeholder="Why is this contract being deactivated?"
					maxLength={500}
				/>
			</Modal>

			<Modal
				title="Cannot activate contract"
				open={collisionModal.open}
				onCancel={handleCollisionClose}
				footer={[
					<Button key="cancel" onClick={handleCollisionClose}>
						Cancel
					</Button>,
					collisionModal.conflict?.id && (
						<Button key="view" type="primary" onClick={handleViewConflicting}>
							View {collisionModal.conflict.id}
						</Button>
					),
				]}
			>
				<p style={{ marginBottom: 12 }}>
					{collisionModal.message ||
						'Another active contract overlaps with this one. Deactivate or expire the conflicting contract first.'}
				</p>
				{collisionModal.conflict && (
					<div
						style={{
							background: '#fafafa',
							border: '1px solid #f0f0f0',
							borderRadius: 6,
							padding: 12,
						}}
					>
						<p style={{ marginBottom: 8, fontWeight: 600 }}>
							Conflicting contract: {collisionModal.conflict.id}
						</p>
						{CONFLICT_FIELDS.map(({ key, label }) =>
							collisionModal.conflict?.[key] ? (
								<p key={key} style={{ marginBottom: 4 }}>
									<span style={{ color: '#8c8c8c' }}>{label}:</span>{' '}
									<span style={{ fontWeight: 500 }}>
										{collisionModal.conflict[key]}
									</span>
								</p>
							) : null
						)}
					</div>
				)}
			</Modal>
		</Box>
	);
}
